import { useState, useEffect, useCallback } from 'react';

const DISMISS_SESSION_KEY = 'qawaem_pwa_install_dismissed';
const DISMISS_PERSISTENT_KEY = 'pwaInstallPromptSeen';

export function isPwaInstalled() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true
  );
}

export function isIosSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIos && isSafari;
}

export function isLikelyIncognito() {
  if (typeof window === 'undefined') return false;
  try {
    const key = '__qawaem_incognito_test__';
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
  } catch {
    return true;
  }
  return false;
}

export function getInstallHelpPlatform() {
  if (isIosSafari()) return 'ios';
  const ua = navigator.userAgent || '';
  if (/Edg\//.test(ua)) return 'edge';
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'chrome';
  if (/Firefox\//.test(ua)) return 'firefox';
  return 'generic';
}

/**
 * Captures beforeinstallprompt and exposes install UI for demo entry.
 */
export default function usePwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIos] = useState(() => isIosSafari());
  const [incognito] = useState(() => isLikelyIncognito());

  useEffect(() => {
    if (isPwaInstalled()) return undefined;
    if (sessionStorage.getItem(DISMISS_SESSION_KEY) === '1') return undefined;
    if (localStorage.getItem(DISMISS_PERSISTENT_KEY) === 'true') return undefined;

    setVisible(true);

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    };
  }, []);

  const install = useCallback(async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setVisible(false);
          setDeferredPrompt(null);
          return { status: 'accepted' };
        }
        return { status: 'dismissed' };
      } catch {
        return { status: 'manual' };
      }
    }
    return { status: 'manual' };
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(DISMISS_SESSION_KEY, '1');
    localStorage.setItem(DISMISS_PERSISTENT_KEY, 'true');
    setVisible(false);
  }, []);

  return {
    visible: visible && !isPwaInstalled(),
    canNativeInstall: Boolean(deferredPrompt),
    isIos,
    incognito,
    platform: getInstallHelpPlatform(),
    install,
    dismiss,
    isInstalled: isPwaInstalled(),
  };
}
